import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { ChessQueen } from "@/components/ui/ChessQueen";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCurrentPlan } from "@/hooks/useCurrentPlan";
import { hasPlanAccess, PartnerPlan, PLAN_LABEL } from "@/lib/planAccess";

interface PlanGateProps {
  requiredPlan: PartnerPlan;
  children: ReactNode;
  /** Quando true, renderiza nada em vez do CTA (útil para esconder seções) */
  silent?: boolean;
  featureName?: string;
}

const PlanGate = ({ requiredPlan, children, silent, featureName }: PlanGateProps) => {
  const { plan, isLoading } = useCurrentPlan();

  if (isLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (hasPlanAccess(plan, requiredPlan)) {
    return <>{children}</>;
  }

  if (silent) return null;

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <Card className="max-w-lg w-full border-primary/30 bg-gradient-to-br from-card via-card to-primary/5">
        <CardContent className="p-8 text-center space-y-4">
          <div className="w-14 h-14 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="w-7 h-7 text-primary" />
          </div>
          <div className="space-y-2">
            <Badge variant="secondary" className="gap-1">
              <ChessQueen className="w-3 h-3" />
              Recurso exclusivo
            </Badge>
            <h2 className="text-xl font-semibold">
              {featureName ? `${featureName} ` : ""}Disponível no plano {PLAN_LABEL[requiredPlan]}
            </h2>
            <p className="text-sm text-muted-foreground">
              Seu plano atual{plan ? ` (${PLAN_LABEL[plan]})` : ""} não inclui este recurso. Faça upgrade para desbloquear.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
            <Button asChild>
              <Link to="/dashboard/assinatura">Fazer upgrade</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/dashboard">Voltar ao Dashboard</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlanGate;
